// =============================================================================
// src/lib/realtime/presenceCore.js - pure helpers for Realtime topics and
// presence (no Supabase import, so node --test can load it).
//
// Realtime Authorization (20270217100000_realtime_authorization): every
// channel the app opens is PRIVATE and its topic starts with a scope and an
// id that the realtime.messages policies check:
//   presence:<practice_id>:<what>   staff of that practice (track + read)
//   practice:<practice_id>:<what>   staff of that practice (Postgres Changes)
//   patient:<patient_id>:<what>     that portal patient (Postgres Changes)
// Topics carry ids only. A patient's NAME must never be in a topic: topics
// appear in socket frames, Realtime logs and the dashboard inspector.
// =============================================================================

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = (v) => typeof v === "string" && UUID.test(v);

// Leave a presence room once the tab has been hidden this long, so a chart
// left open in a background tab overnight does not claim someone is "viewing".
export const PRESENCE_HIDDEN_LEAVE_MS = 2 * 60 * 1000;

// Avatars shown before "+N".
export const PRESENCE_MAX_SHOWN = 3;

// The last segment is a free label (table names, screen names); keep it to
// characters that cannot add a ":" segment or smuggle anything odd.
const cleanLabel = (s) => String(s || "").replace(/[^A-Za-z0-9_.,-]/g, "-").slice(0, 120);

export function practiceTopic(practiceId, label) {
  if (!isUuid(practiceId)) return null;
  return "practice:" + practiceId.toLowerCase() + ":" + cleanLabel(label);
}

export function patientTopic(patientId, label) {
  if (!isUuid(patientId)) return null;
  return "patient:" + patientId.toLowerCase() + ":" + cleanLabel(label);
}

// presence:<practice>:worklist, presence:<practice>:chart:<patient uuid>,
// presence:<practice>:appt:<appointment uuid>. A subject id, when given, must
// be a uuid: anything else (a name, an MRN) is refused rather than sent.
export function presenceTopic(practiceId, kind, subjectId) {
  if (!isUuid(practiceId)) return null;
  const k = cleanLabel(kind);
  if (!k) return null;
  if (subjectId === undefined || subjectId === null) return "presence:" + practiceId.toLowerCase() + ":" + k;
  if (!isUuid(subjectId)) return null;
  return "presence:" + practiceId.toLowerCase() + ":" + k + ":" + subjectId.toLowerCase();
}

// "Ana Rodriguez" -> "Ana R."; "ana" -> "ana"; email -> local part.
export function shortName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "Someone";
  const base = raw.includes("@") && !raw.includes(" ") ? raw.split("@")[0] : raw;
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || "Someone";
  return parts[0] + " " + parts[parts.length - 1].charAt(0).toUpperCase() + ".";
}

export function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const a = parts[0].charAt(0);
  const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (a + b).toUpperCase();
}

// The payload a staff member publishes: user id, display name, role. Nothing
// about the patient or appointment (the topic already says which one).
export function presencePayload(me) {
  if (!me || !isUuid(me.id)) return null;
  return {
    uid: me.id,
    name: String(me.name || "Staff").slice(0, 80),
    role: me.role ? String(me.role).slice(0, 40) : null,
  };
}

// presenceState() -> the OTHER people in the room, one entry per user (a user
// with the chart open in two tabs shows once), sorted by name so avatars do
// not reshuffle on every sync. Malformed metas are skipped.
export function reducePresence(state, selfUid) {
  const byUid = new Map();
  if (!state || typeof state !== "object") return [];
  for (const key of Object.keys(state)) {
    const metas = Array.isArray(state[key]) ? state[key] : [];
    for (const m of metas) {
      if (!m || typeof m !== "object") continue;
      const uid = isUuid(m.uid) ? m.uid : (isUuid(key) ? key : null);
      if (!uid || uid === selfUid) continue;
      if (byUid.has(uid)) continue;
      byUid.set(uid, {
        uid,
        name: typeof m.name === "string" && m.name.trim() ? m.name.trim() : "Staff",
        role: typeof m.role === "string" ? m.role : null,
      });
    }
  }
  return Array.from(byUid.values()).sort((a, b) => a.name.localeCompare(b.name) || a.uid.localeCompare(b.uid));
}

// What the avatar row shows: up to max avatars, "+N" for the rest, and the
// screen-reader sentence ("Also viewing: Ana R. and Ben T.").
export function viewerSummary(viewers, max = PRESENCE_MAX_SHOWN) {
  const list = Array.isArray(viewers) ? viewers : [];
  const shown = list.slice(0, max);
  const extra = Math.max(0, list.length - shown.length);
  const names = list.map((v) => shortName(v.name));
  let sr = "";
  if (names.length === 1) sr = "Also viewing: " + names[0];
  else if (names.length === 2) sr = "Also viewing: " + names[0] + " and " + names[1];
  else if (names.length > 2) sr = "Also viewing: " + names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
  return { shown, extra, srText: sr, count: list.length };
}

// Two viewer lists are the same for rendering purposes (skip a re-render on a
// sync that changed nothing visible).
export function sameViewers(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].uid !== b[i].uid || a[i].name !== b[i].name || a[i].role !== b[i].role) return false;
  }
  return true;
}
